/**
 * Hook and utilities for managing household profile creation.
 */

import { useState, useEffect } from 'react';
import type { Profile } from '@yada/types';
import { getProfilesForCurrentAccount, createProfile, getCurrentAccount } from './accountService';
import { createProfile as createProfileServer } from '@yada/api-client';
import { getSessionToken } from '@yada/storage';

/**
 * Check if user has a household profile
 */
export async function hasHouseholdProfile(): Promise<boolean> {
  const profiles = await getProfilesForCurrentAccount();
  return profiles.some(p => p.profileType === 'household');
}

/**
 * Get household profile if it exists
 */
export async function getHouseholdProfile(): Promise<Profile | null> {
  const profiles = await getProfilesForCurrentAccount();
  return profiles.find(p => p.profileType === 'household') || null;
}

/**
 * Create a household profile
 * Server auto-creates a household workspace and returns it
 */
export async function createHouseholdProfile(storageUrl?: string): Promise<Profile & { workspaceId?: string }> {
  const account = await getCurrentAccount();
  if (!account) {
    throw new Error('No account found');
  }

  const token = await getSessionToken();
  const profileName = 'Household';

  // Create profile locally
  let profile: Profile & { workspaceId?: string };

  if (token) {
    try {
      // Try to create on server first
      const serverProfile = await createProfileServer(token, {
        name: profileName,
        profile_type: 'household',
        visibility: 'workspace',
        metadata: {},
        storage_url: storageUrl,
      });

      profile = {
        id: serverProfile.id,
        accountId: serverProfile.account_id,
        profileType: serverProfile.profile_type as any,
        name: serverProfile.name,
        verificationLevel: serverProfile.verification_level,
        visibility: serverProfile.visibility as any,
        metadata: serverProfile.metadata,
        createdAt: new Date(serverProfile.created_at),
        updatedAt: new Date(serverProfile.updated_at),
      };

      // Note: Server may auto-create workspace, but it's not returned in profile response
      // Workspace will be fetched separately when needed

      // Store locally
      const { setProfile } = await import('./accountService');
      await setProfile(profile);
    } catch (err) {
      console.error('Failed to create household profile on server:', err);
      // Fallback: create locally
      profile = await createProfile({
        name: profileName,
        profileType: 'household',
        visibility: 'workspace',
        metadata: {},
      });
    }
  } else {
    // No token - create locally only
    profile = await createProfile({
      name: profileName,
      profileType: 'household',
      visibility: 'workspace',
      metadata: {},
    });
  }

  return profile;
}

/**
 * Hook to check household profile status
 */
export function useHouseholdProfile() {
  const [hasHousehold, setHasHousehold] = useState<boolean | null>(null);
  const [householdProfile, setHouseholdProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      setIsLoading(true);
      const exists = await hasHouseholdProfile();
      const profile = exists ? await getHouseholdProfile() : null;
      setHasHousehold(exists);
      setHouseholdProfile(profile);
      setIsLoading(false);
    };
    check();
  }, []);

  const createHousehold = async () => {
    const profile = await createHouseholdProfile();
    setHasHousehold(true);
    setHouseholdProfile(profile);
    return profile;
  };

  return {
    hasHousehold,
    householdProfile,
    isLoading,
    createHousehold,
  };
}
